"use client";

import { useAuth } from "@/components/AuthContext";
import { ArrowLeft, LogIn, UserRound, UserRoundPlus } from "lucide-react";

interface AccountAccessProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  isAccountActive?: boolean;
  onAccountClick: () => void;
  variant?: "default" | "ecole" | "liceo";
}

export function AccountAccess({
  isAuthenticated,
  isLoading,
  isAccountActive = false,
  onAccountClick,
  variant = "default",
}: AccountAccessProps) {
  const { openAuthModal } = useAuth();
  const focusColor =
    variant !== "default" ? "focus-visible:ring-school-focus" : "focus-visible:ring-orange-300";
  const primaryColor =
    variant !== "default"
      ? "border-school-accent/55 bg-school-accent text-school-on-accent hover:bg-school-accent-bright"
      : "border-orange-300/70 bg-orange-500 text-[#171716] hover:bg-orange-600";

  if (isLoading) {
    return (
      <div className="h-10 w-20 animate-pulse rounded-lg bg-white/[0.06]" aria-hidden="true" />
    );
  }

  if (isAuthenticated) {
    return (
      <button
        type="button"
        onClick={onAccountClick}
        className={`inline-flex h-10 items-center gap-2 rounded-lg border border-white/12 bg-black/20 px-3 text-sm font-semibold text-white/85 transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 ${focusColor}`}
        aria-label={isAccountActive ? "Back to speaking practice" : "Open account"}
      >
        {isAccountActive ? (
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        ) : (
          <UserRound className="h-4 w-4" aria-hidden="true" />
        )}
        <span className="hidden sm:inline">{isAccountActive ? "Practice" : "Account"}</span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => openAuthModal("signin")}
        className={`inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-lg border border-white/12 bg-black/20 px-2 text-sm font-semibold text-white/90 transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 sm:gap-2 sm:px-3 ${focusColor}`}
      >
        <LogIn className="h-4 w-4 sm:hidden" aria-hidden="true" />
        <span>Sign in</span>
      </button>
      <button
        type="button"
        onClick={() => openAuthModal("signup")}
        className={`hidden h-10 items-center gap-2 rounded-lg border px-3 text-sm font-bold shadow-lg shadow-black/15 transition-colors focus-visible:outline-none focus-visible:ring-2 sm:inline-flex ${focusColor} ${primaryColor}`}
      >
        <UserRoundPlus className="h-4 w-4" aria-hidden="true" />
        Start free
      </button>
    </div>
  );
}
